
import { ODataMessage } from "./message";
import { ModelBase } from "../../models/abstract/model-base.model";

export class ODataListResponse {
    count: number;
    results: any[] = [];

    constructor(jsonResponse: any) {
        if (!!jsonResponse.results) {
            this.results = jsonResponse.results;
            this.count = jsonResponse.count;
        } else {
            this.results = jsonResponse;
            this.count = this.results.length;
        }
    }
}

export class ODataResponse {
    messages: ODataMessage[] = [];
    value: any;

    constructor(jsonResponse: any) {
        for (var key in jsonResponse.messages) {
            this.messages.push(new ODataMessage(jsonResponse.messages[key]));
        }
        this.value = jsonResponse.value;
    }
}

export class ODataError {
    messages: ODataMessage[] = [];
    details: any;
    status: number;
    statusText: string;
    url: string;

    /** Corps de la réponse d'erreur, tel que reçu : la matière des règles ci-dessous */
    private _body: any;
    /** Identifiant de requête fourni par le serveur, reporté sur chaque message */
    private _uuid: string;

    /**
     * Le constructeur ne fait que retenir la réponse brute. Il est privé : on passe par
     * `fromResponse()`, qui construit **et** bâtit les messages d'un seul geste. Un objet
     * construit sans ses messages rendrait l'échec silencieux, la pire des issues.
     */
    private constructor(response: any) {
        this.details = {};
        this.messages = [];
        this.status = response.status;
        this.statusText = response.statusText;
        this.url = response.url;
        this._body = response.error;
        this._uuid = response.error?.uuid;
    }

    /*----------------------------------------------------------------------------------------
     * Accesseurs
     ----------------------------------------------------------------------------------------*/

    /**
     * Le serveur a refusé l'envoi parce qu'un sous-objet envoyé avec son identifiant n'existe
     * plus — une imputation supprimée par l'enregistrement précédent, ou remplacée par la
     * proposition d'imputation IA entre deux enregistrements (#1155). L'objet principal, lui,
     * existe : l'écran est bâti sur un état périmé, et doit repartir de celui du serveur.
     */
    public get staleObject(): boolean {
        return this.messages.some(message => ['NESTED_OBJECT_NOT_FOUND', 'LINKED_OBJECT_NOT_FOUND'].includes(message.code));
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /**
     * Construit l'erreur à partir de la réponse HTTP et bâtit ses messages.
     * @param response La réponse d'erreur HTTP (`HttpErrorResponse`)
     * @param instance L'instance envoyée, quand il y en a une : le contexte que le serveur n'a pas
     */
    public static fromResponse(response: any, instance?: ModelBase): ODataError {
        const error = new ODataError(response);
        error._buildMessages(instance);
        return error;
    }

    /**
     * Erreurs des éléments refusés d'une liste de sous-objets (#1168), par position.
     *
     * La position est l'indice de l'élément **dans la liste envoyée**, à partir de 0 ; ses
     * erreurs sont rangées par champ, le chemin de l'élément retiré :
     * `subObjectErrors('item')` rend `18 → { productName: … }` pour une erreur sous
     * `item18.productName`. Le message, lui, désigne déjà l'élément (« Ligne 19 — … ») :
     * cette lecture ne sert qu'à retrouver l'élément et son champ à l'écran.
     * @param prefix Le préfixe de la clé de l'élément : `item`, `allocations`
     */
    public subObjectErrors(prefix: string): Map<number, Record<string, ODataMessage>> {
        const pattern = new RegExp(`^${prefix}(\\d+)\\.(.+)$`);
        const errors = new Map<number, Record<string, ODataMessage>>();
        for (const key of Object.keys(this.details)) {
            const match = key.match(pattern);
            if (!match) continue;
            const index = Number(match[1]);
            errors.set(index, { ...errors.get(index), [match[2]]: this.details[key] });
        }
        return errors;
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    /**
     * Orchestration des règles, dans l'ordre : ce que le serveur a dit, ce qu'on invente quand
     * il n'a rien dit, puis ce qu'on complète avec le contexte que seul le client détient.
     */
    private _buildMessages(instance?: ModelBase): void {
        this._readServerMessages();
        this._readFieldErrors();
        if (this.messages.length === 0) {
            this._defaultMessageByStatus();
        }
        this._nameAttachmentOnIncompleteBody(instance);
    }

    /**
     * Sans message serveur : un message de repli selon le code HTTP. Les codes absents de la
     * liste ne produisent rien.
     */
    private _defaultMessageByStatus(): void {
        switch (this.status) {
            case 401: {
                if (this._body?.detail === 'Given token not valid for any token type' || this._body?.detail === 'Logged out successfully') return;
                let longText = this._body?.detail.startsWith('No active') ? 'Identifiants de connexion incorrects' : this._body?.detail === 'Token is expired' ? 'Votre session a expiré' : 'Non autorisé';
                let detail = new ODataMessage({
                    domain: 'http',
                    longText: longText,
                    number: 401,
                    shortText: 'Non autorisé',
                    type: 'E',
                    uuid: this._uuid
                });
                this.details = { authorization: detail };
                this.messages.push(detail);
                break;
            }
            case 403: {
                let detail = new ODataMessage({
                    domain: 'http',
                    longText: `Vous n'êtes pas autorisé à effectuer cette action`,
                    number: 403,
                    shortText: 'Non autorisé',
                    type: 'E',
                    uuid: this._uuid
                });
                this.details = { authorization: detail };
                this.messages.push(detail);
                break;
            }
            case 404: {
                let detail = new ODataMessage({
                    domain: 'http',
                    longText: `L'élément demandé n'a pu être trouvé`,
                    number: 404,
                    shortText: 'Ressource indisponible',
                    type: 'E',
                    uuid: this._uuid
                });
                this.details = { authorization: detail };
                this.messages.push(detail);
                break;
            }
            case 500: {
                let detail = new ODataMessage({
                    domain: 'http',
                    longText: 'Si l\'erreur persiste, veuillez contacter le support technique.',
                    number: 500,
                    shortText: 'Erreur backend',
                    type: 'E',
                    uuid: this._uuid
                });
                this.details = { server: detail };
                this.messages.push(detail);
                break;
            }
            case 504: {
                let detail = new ODataMessage({
                    domain: 'network',
                    longText: "Une erreur est survenue lors de l'envoi de votre document à cause d'une erreur de réseau. Veuillez réessayer ultérieurement.",
                    number: 504,
                    shortText: "Erreur lors de l'envoi du document",
                    type: 'E',
                    uuid: this._uuid
                });
                this.details = { timeout: detail };
                this.messages.push(detail);
                break;
            }
        }
    }

    /**
     * Corps de requête arrivé incomplet au serveur (`INCOMPLETE_REQUEST_BODY`, #1106).
     *
     * Le serveur ne sait pas quelle pièce il a refusée : le corps n'est pas arrivé entier,
     * il n'a rien pu lire. Seul le client le sait. Quand l'instance envoyée portait une
     * pièce jointe (dépôt en base64, cf. `docs/utilities/FILE_UPLOAD_BASE64.md`), son nom
     * est ajouté au titre du message, sinon l'utilisateur d'un dépôt en lot ne sait pas
     * quel fichier redéposer. Le texte du message reste celui du serveur.
     */
    private _nameAttachmentOnIncompleteBody(instance?: ModelBase): void {
        if (!instance?.attachmentName) return;
        for (const message of this.messages) {
            if (message.code === 'INCOMPLETE_REQUEST_BODY') {
                message.shortText = `${message.shortText} : ${instance.attachmentName}`;
            }
        }
    }

    /** Erreurs de champ (`errors`) : un message par champ, dans `details` */
    private _readFieldErrors(): void {
        if (!!this._body && !!this._body.errors) {
            this._readFieldErrorsAt(this._body.errors, '');
        }
    }

    /**
     * Lecture récursive de `errors` (#1168).
     *
     * Un sous-objet refusé porte ses erreurs sous la clé de l'élément (`item18`,
     * `allocations0`), qui peut elle-même contenir un sous-élément (`item1.allocations0`).
     * Une entrée qui porte un `code` ou un `longText` est un message ; toute autre est un
     * niveau à descendre. Le message est rangé sous son chemin complet :
     * `details['item18.productName']`. Contrat : `b4cash-srv/documentation/architecture/SUB_OBJECT_ERRORS.md` §5.
     * @param errors Le niveau de `errors` en cours de lecture
     * @param path Le chemin du niveau, terminé par un point (vide à la racine)
     */
    private _readFieldErrorsAt(errors: any, path: string): void {
        for (const key in errors) {
            const entry = errors[key];
            if (entry instanceof Object && !('code' in entry) && !('longText' in entry)) {
                this._readFieldErrorsAt(entry, `${path}${key}.`);
                continue;
            }
            let message: ODataMessage = new ODataMessage(entry);
            message.uuid = this._uuid;
            this.details[`${path}${key}`] = message;
        }
    }

    /** Messages du référentiel serveur (`messages`) */
    private _readServerMessages(): void {
        if (!!this._body && !!this._body.messages) {
            for (var key in this._body.messages) {
                let message: ODataMessage = new ODataMessage(this._body.messages[key]);
                message.uuid = this._uuid;
                this.messages.push(message);
            }
        }
    }
}
